/**
 * An invoice a clinic emitted for a sale.
 *
 * One invoice belongs to one purchase, and a purchase has at most one that is not cancelled.
 * Cancelled ones stay in the list: a patient's file shows what happened, not only what stands,
 * and a document that existed and was withdrawn is a different fact from no document.
 *
 * The receipt photograph is deliberately not in here. It is one request away at
 * `/invoices/{id}/receipt` and is worth hundreds of kilobytes, which is not a thing to carry
 * in a list that loads every time somebody opens a patient.
 */

import { IPatientPurchase } from './patientPurchase';

/**
 * Where an invoice stands with its provider.
 *
 * `PENDING` is the state a row is written in before the provider is called, so one that stays
 * there is an attempt whose answer was never seen -- rare, and safe to retry, because the
 * idempotency key the server keeps is what stops a second document being made.
 */
export type InvoiceStatus = 'PENDING' | 'ISSUED' | 'FAILED' | 'CANCELLED';

export interface IInvoice {
    id: number;
    purchaseId: number;
    status: InvoiceStatus;
    provider: string;
    seriesName: string | null;
    number: string | null;
    /** `FCT-0053`. Composed server-side so it reads the same everywhere. */
    documentNumber: string | null;
    /** Where the PDF can be read, at the provider. Null until issued. */
    documentUrl: string | null;
    clientName: string;
    amount: number;
    currency: string;
    receiptNumber: string;
    /** ISO date. */
    receiptDate: string;
    issuedAt: string | null;
    cancelledAt: string | null;
    /**
     * The provider's own words for the last failure. Shown rather than hidden: "seria FCT nu
     * exista" is something the therapist can act on, and "an error occurred" would send them
     * to an administrator who knows less about the sale than they do.
     */
    lastError: string | null;
}

/** Who the invoice is made out to. Prefilled from the patient, but not the same thing. */
export interface InvoiceClient {
    name: string;
    cif?: string;
    address?: string;
    city?: string;
    state?: string;
    email?: string;
}

/** The till receipt this invoice accompanies. */
export interface InvoiceReceipt {
    number: string;
    /** ISO date. */
    date: string;
    amount: number;
}

/**
 * The JSON half of POST /invoices. The photograph is the other part of the same multipart
 * request, so there is never an uploaded image with no invoice to belong to.
 *
 * No amount: it comes from the purchase, server-side, the same rule the sale itself follows.
 */
export interface EmitInvoiceRequest {
    purchaseId: number;
    client: InvoiceClient;
    receipt: InvoiceReceipt;
}

/** How long after a sale an invoice can still be emitted. Mirrors the server's own rule. */
export const INVOICE_WINDOW_MONTHS = 1;

/**
 * Whether this sale can still be invoiced.
 *
 * The same arithmetic the API does, so the button is offered exactly when pressing it would
 * work. Compared as dates rather than instants because `assignedOn` is a date -- the sale's
 * own day, which a therapist may have back-dated when typing up a walk-in.
 */
export const isWithinInvoiceWindow = (purchase: IPatientPurchase, today: Date = new Date()): boolean => {
    const soldOn: Date = new Date(`${purchase.assignedOn}T00:00:00`);
    const deadline: Date = new Date(soldOn);

    deadline.setMonth(deadline.getMonth() + INVOICE_WINDOW_MONTHS);

    const midnightToday: Date = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return midnightToday.getTime() <= deadline.getTime();
};

/** The purchase's live invoice, if it has one. Cancelled ones are history, not the answer. */
export const liveInvoiceFor = (
    purchase: IPatientPurchase,
    invoices: IInvoice[]
): IInvoice | undefined =>
    invoices.find(
        (invoice: IInvoice) =>
            invoice.purchaseId === purchase.id && invoice.status !== 'CANCELLED'
    );
